import React from 'react';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { useCmsDoc } from '../hooks/useCms';
import { FALLBACK_ABOUT } from '../data/cmsFallbacks';
import { Quote, Award, Calendar, MapPin, Star, Shield, Users } from 'lucide-react';
import SEO from '../components/SEO';
import { seoData, translations } from '../data/translations';
import { useLanguage } from '../context/LanguageContext';

// Golden Glow Line Component
const GoldenGlowLine = () => (
  <div className="relative h-[2px] bg-black overflow-hidden">
    <div 
      className="absolute top-0 h-full w-[300px]"
      style={{
        background: 'linear-gradient(90deg, transparent, #D4AF37, #FFD700, #D4AF37, transparent)',
        animation: 'glowLine 4s ease-in-out infinite',
        boxShadow: '0 0 20px #D4AF37, 0 0 40px #D4AF37',
      }}
    />
    <style>{`
      @keyframes glowLine {
        0% { left: -300px; opacity: 0; }
        15% { opacity: 1; }
        85% { opacity: 1; }
        100% { left: 100%; opacity: 0; }
      }
    `}</style>
  </div>
);

// Animated Corner Frame Component
const AnimatedCornerFrame = ({ children, className = "" }) => (
  <div className={`group relative ${className}`}>
    {/* Top-left corner frame */}
    <div className="absolute top-0 left-0 w-1/2 h-1/2 overflow-hidden rounded-tl-3xl pointer-events-none">
      <div className="absolute top-0 left-0 w-[2px] h-full bg-black">
        <div 
          className="absolute left-0 w-full h-[60px]"
          style={{
            background: 'linear-gradient(180deg, transparent, #D4AF37, #FFD700, #D4AF37, transparent)',
            animation: 'cornerGlowVertical 4s ease-in-out infinite',
            boxShadow: '0 0 15px #D4AF37, 0 0 30px #D4AF37',
          }}
        />
      </div>
      <div className="absolute top-0 left-0 h-[2px] w-full bg-black">
        <div 
          className="absolute top-0 h-full w-[60px]"
          style={{
            background: 'linear-gradient(90deg, transparent, #D4AF37, #FFD700, #D4AF37, transparent)',
            animation: 'cornerGlowHorizontal 4s ease-in-out infinite',
            boxShadow: '0 0 15px #D4AF37, 0 0 30px #D4AF37',
          }}
        />
      </div>
    </div>
    
    {/* Bottom-right corner frame */}
    <div className="absolute bottom-0 right-0 w-1/2 h-1/2 overflow-hidden rounded-br-3xl pointer-events-none">
      <div className="absolute bottom-0 right-0 w-[2px] h-full bg-black">
        <div 
          className="absolute right-0 w-full h-[60px]"
          style={{
            background: 'linear-gradient(0deg, transparent, #D4AF37, #FFD700, #D4AF37, transparent)',
            animation: 'cornerGlowVerticalReverse 4s ease-in-out infinite',
            boxShadow: '0 0 15px #D4AF37, 0 0 30px #D4AF37',
          }}
        />
      </div>
      <div className="absolute bottom-0 right-0 h-[2px] w-full bg-black">
        <div 
          className="absolute bottom-0 h-full w-[60px]"
          style={{
            background: 'linear-gradient(270deg, transparent, #D4AF37, #FFD700, #D4AF37, transparent)',
            animation: 'cornerGlowHorizontalReverse 4s ease-in-out infinite',
            boxShadow: '0 0 15px #D4AF37, 0 0 30px #D4AF37',
          }}
        />
      </div>
    </div>
    {children}
    <style>{`
      @keyframes cornerGlowVertical {
        0% { top: -40px; opacity: 0; }
        15% { opacity: 1; }
        85% { opacity: 1; }
        100% { top: 100%; opacity: 0; }
      }
      @keyframes cornerGlowHorizontal {
        0% { left: -40px; opacity: 0; }
        15% { opacity: 1; }
        85% { opacity: 1; }
        100% { left: 100%; opacity: 0; }
      }
      @keyframes cornerGlowVerticalReverse {
        0% { bottom: -40px; opacity: 0; }
        15% { opacity: 1; }
        85% { opacity: 1; }
        100% { bottom: 100%; opacity: 0; }
      }
      @keyframes cornerGlowHorizontalReverse {
        0% { right: -40px; opacity: 0; }
        15% { opacity: 1; }
        85% { opacity: 1; }
        100% { right: 100%; opacity: 0; }
      }
    `}</style>
  </div>
);

const AboutPage = () => {
  const { t } = useLanguage();
  const about = useCmsDoc('/api/cms/about', FALLBACK_ABOUT);
  
  const stats = [
    { icon: Calendar, val: '2018', labelDe: 'Gegründet', labelEn: 'Founded' },
    { icon: Award, val: '5-Sterne', labelDe: 'Hotellerie', labelEn: 'Hospitality' },
    { icon: MapPin, val: 'Berlin', labelDe: '& Brandenburg', labelEn: '& Brandenburg' }
  ];

  const values = [
    { icon: Star, titleDe: 'Exzellenz', titleEn: 'Excellence', descDe: 'Höchste Qualität in allem.', descEn: 'Highest quality in everything.' },
    { icon: Shield, titleDe: 'Zuverlässigkeit', titleEn: 'Reliability', descDe: 'Rund um die Uhr verlässlich.', descEn: 'Reliable around the clock.' },
    { icon: Users, titleDe: 'Professionalität', titleEn: 'Professionalism', descDe: 'Höchste Standards.', descEn: 'Highest standards.' }
  ];

  const bioParagraphs = (about.bio_de || []).map((para, i) => ({ de: para, en: (about.bio_en || [])[i] || para }));
  
  return (
    <div className="min-h-screen bg-[#F9F9F9]">
      <SEO 
        title={seoData.about.title}
        description={seoData.about.description}
      />
      <Navbar />
      <main>
        <section className="relative pt-40 pb-20 overflow-hidden">
          <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-20">
              <div className="inline-flex items-center gap-3 mb-6">
                <div className="h-px w-12 bg-[#D4AF37]" />
                <span className="text-black/50 text-sm font-medium uppercase tracking-[0.3em]">{t('about', translations.nav)}</span>
                <div className="h-px w-12 bg-[#D4AF37]" />
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-black">{t('subtitle', translations.about)}</h1>
            </div>

            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <AnimatedCornerFrame className="relative rounded-3xl overflow-hidden">
                <img src={about.image_url} alt={about.name} className="w-full h-auto object-contain" />
                <div className="absolute bottom-0 left-0 right-0 p-8">
                  <div className="bg-white/95 backdrop-blur-xl rounded-2xl p-6 border border-black/10">
                    <h2 className="text-2xl font-bold text-black mb-1">{about.name}</h2>
                    <p className="text-[#D4AF37] font-medium">{t('role', { de: about.role_de, en: about.role_en })}</p>
                  </div>
                </div>
              </AnimatedCornerFrame>

              <div>
                <div className="w-16 h-16 rounded-2xl bg-[#D4AF37]/10 flex items-center justify-center mb-8">
                  <Quote className="w-8 h-8 text-[#D4AF37]" />
                </div>
                <div className="space-y-6">
                  {bioParagraphs.map((paragraph, index) => (
                    <p key={index} className="text-black/70 text-lg leading-relaxed">{t('bio', paragraph)}</p>
                  ))}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-12">
                  {stats.map((item, i) => (
                    <div key={i} className="p-6 rounded-2xl bg-white border border-black/5 shadow-lg shadow-black/5">
                      <item.icon className="w-6 h-6 text-[#D4AF37] mb-3" />
                      <p className="text-2xl font-bold text-black">{item.val}</p>
                      <p className="text-black/60 text-sm">{t('label', { de: item.labelDe, en: item.labelEn })}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <GoldenGlowLine />

        <section className="py-32 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-black mb-6">{t('values', translations.about)}</h2>
              <p className="text-black/60 text-lg max-w-2xl mx-auto">{t('valuesDesc', { de: 'Diese Prinzipien leiten uns bei allem, was wir tun', en: 'These principles guide us in everything we do' })}</p>
            </div>
            <div className="grid md:grid-cols-3 gap-8">
              {values.map((v, i) => (
                <AnimatedCornerFrame key={i} className="p-8 rounded-3xl bg-[#F9F9F9] border border-black/5 hover:border-[#D4AF37]/30 transition-all duration-500 hover:shadow-xl text-center">
                  <div className="w-16 h-16 rounded-2xl bg-[#D4AF37]/10 flex items-center justify-center mb-6 mx-auto">
                    <v.icon className="w-8 h-8 text-[#D4AF37]" />
                  </div>
                  <h3 className="text-xl font-semibold text-black mb-3">{t('title', { de: v.titleDe, en: v.titleEn })}</h3>
                  <p className="text-black/60 leading-relaxed">{t('desc', { de: v.descDe, en: v.descEn })}</p>
                </AnimatedCornerFrame>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default AboutPage;
